import type { BlueprintsController } from '../types/server/endpoints/blueprints'
import type { BlueprintWithTasks } from '../types/server/models/blueprint'
import { appConfig } from '../configs/app'
import { useUser } from './user-service'
import fetcher from '../utils/fetcher'
import useSWR from 'swr'
import axios from 'axios'

/* GET METHODS */

/**
 * Fetches the user's relevant blueprints.
 */
export const useUserBlueprints = (userId?: string) => {
	const { user } = useUser()

	const fetch = user?.isNewsletterOnly
		? { data: [] as BlueprintWithTasks[], error: null, isLoading: false, mutate: () => {} }
		: useSWR<BlueprintsController['getUserBlueprints']['returns']>(
				`${appConfig.apiUrl}/blueprints/user${userId ? `/${userId}` : ''}`,
				fetcher
		  )

	return {
		...fetch,
		data: fetch.data || [],
	}
}

/**
 * Fetches a single blueprint by its ID with the tasks.
 */
export const useGetBlueprint = (blueprintId?: string) => {
	const fetch = useSWR<BlueprintsController['getBlueprint']['returns']>(
		blueprintId ? `${appConfig.apiUrl}/blueprints/${blueprintId}` : undefined,
		fetcher
	)

	return fetch
}

/**
 * Fetches the blueprints for a specific company.
 */
export const useCompanyBlueprints = (companyId?: string) => {
	const fetch = useSWR<BlueprintsController['getCompanyBlueprints']['returns']>(
		companyId ? `${appConfig.apiUrl}/blueprints/companies/${companyId}` : undefined,
		fetcher
	)

	return {
		...fetch,
		data: fetch.data || [],
	}
}

/**
 * Fetches the progress of tasks for a specific role for the logged user.
 */
export const useTasksProgressByRole = (roleId?: string, userId?: string, read?: boolean) => {
	const { user } = useUser()
	const fetch = user?.isNewsletterOnly
		? { data: null, error: null, isLoading: false, mutate: async () => {} }
		: useSWR<BlueprintsController['getProgressByRoleId']['returns']>(
				roleId
					? `${appConfig.apiUrl}/blueprints/progress/${roleId}${
							userId ? `?userId=${userId}` : read ? '?read=true' : ''
					  }`
					: undefined,
				fetcher
		  )

	return {
		...fetch,
		data: fetch.data || { tasks: [], total: 0, totalChecked: 0 },
	}
}

/**
 * Fetches the progress of tasks for a specific role for the logged user.
 */
export const useTasksProgressByBlueprint = (blueprintId?: string, userId?: string) => {
	const { user } = useUser()
	const fetch = user?.isNewsletterOnly
		? { data: null, error: null, isLoading: false }
		: useSWR<BlueprintsController['getProgressByBlueprintId']['returns']>(
				blueprintId
					? `${appConfig.apiUrl}/blueprints/progress/${blueprintId}${userId ? `?userId=${userId}` : ''}`
					: undefined,
				fetcher
		  )

	return {
		...fetch,
		data: fetch.data || { tasks: [], total: 0, totalChecked: 0 },
	}
}

/**
 * Fetches the progress of tasks for a specific role for the logged user.
 */
export const useTasksProgress = (userId?: string) => {
	const { user } = useUser()
	const fetch = user?.isNewsletterOnly
		? { data: null, error: null, isLoading: false }
		: useSWR<BlueprintsController['getProgressForAllRoles']['returns']>(
				`${appConfig.apiUrl}/blueprints/progress${userId ? `?userId=${userId}` : ''}`,
				fetcher,
				{
					revalidateOnFocus: false,
					revalidateOnReconnect: false,
					refreshInterval: 0,
					refreshWhenHidden: false,
					revalidateIfStale: false,
				}
		  )

	return {
		...fetch,
		data: fetch.data || { tasks: [], total: 0, totalChecked: 0 },
	}
}

/* ACTIONS */
/**
 * Creates a new blueprint.
 */
export const CreateBlueprint = async (body: BlueprintsController['createBlueprint']['body']) => {
	// const response = await server.post('/blueprints', body)
	const response = await axios.post(`${appConfig.apiUrl}/blueprints`, body, {
		headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
	})
	return response.data
}

/**
 * Updates an existing blueprint.
 */
export const UpdateBlueprint = async (
	id: BlueprintsController['updateBlueprint']['params']['id'],
	body: BlueprintsController['updateBlueprint']['body']
) => {
	// const response = await server.put(`/blueprints/${id}`, body)
	const response = await axios.put(`${appConfig.apiUrl}/blueprints/${id}`, body, {
		headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
	})
	return response.data
}

/**
 * Deletes a blueprint.
 */
export const DeleteBlueprint = async (id: BlueprintsController['deleteBlueprint']['params']['id']) => {
	// const response = await server.delete(`/blueprints/${id}`)
	const response = await axios.delete(`${appConfig.apiUrl}/blueprints/${id}`, {
		headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
	})
	return response.data
}

/**
 * Submits a blueprint.
 */
export const SubmitBlueprint = async (body: BlueprintsController['submitBlueprint']['body']) => {
	// const response = await server.post('/blueprints/progress', body)
	const response = await axios.post(`${appConfig.apiUrl}/blueprints/progress`, body, {
		headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
	})
	return response.data
}
